import { reactive, ref } from "vue";
import { ServiceFromServer } from "./service";

export function formatSpecs(service: ServiceFromServer) {
    function planCodeConverter(group: number) {
        if (group === 61) {
            // "Standard (Promo)"
            group = 2;
        }
        if ([63, 64].includes(group)) {
            // "Premium (Promo)"
            group = 3;
        }
        return JSON.parse(JSON.stringify(modelSpecs[{
            1: "Free",
            2: "Standard",
            3: "Premium",
            50: "Unlimited",
            51: "Standard (Perpetual License)",
            52: "Premium (Perpetual License)",
        }[group]]));
    }

    let currentPlan = planCodeConverter(service.group);

    function numberToStringWithUnits(num: number) {
        if (num === 0) return "0";
        if (num >= 1e12) return (num / 1e12).toFixed(2) + "T";
        if (num >= 1e9) return (num / 1e9).toFixed(2) + "B";
        if (num >= 1e6) return (num / 1e6).toFixed(2) + "M";
        if (num >= 1e3) return (num / 1e3).toFixed(2) + "K";
        return num.toString();
    }

    const databaseUsage = (service.storage_info?.database || 0) + (service.storage_info?.cloud || 0);

    let serviceStatus = {
        id: service.projectId,
        original_service_id: service.service,
        statusCode: service.active,
        name: service.name,
        status: {
            "0": "Disabled",
            "1": "Running",
            "-1": "Suspended",
            "-2": "Deleted",
            "-999": "Banned"
        }[service.active.toString()],
        canChangePlan: !currentPlan.label.includes("Perpetual") && currentPlan.label !== "Unlimited",
        users_string: numberToStringWithUnits(service.users),
        users_percent: `${getPercentUsed(service.users, currentPlan.users === 0 ? 0 : currentPlan.users)}%`,
        users: service.users,
        newsletter_subscribers_string: numberToStringWithUnits(service.newsletter_subscribers),
        newsletter_subscribers: service.newsletter_subscribers,
        storage_bytes: {
            ...service.storage_info,
            database: databaseUsage,
        },
        storage: {
            database: convertBytesToReadableStorage(databaseUsage),
            database_percent: `${getPercentUsed(databaseUsage, currentPlan.storage.database === "Unlimited" ? 0 : currentPlan.storage_bytes.database)}%`,
            cloud: convertBytesToReadableStorage(service.storage_info?.cloud),
            cloud_percent: `${getPercentUsed(service.storage_info?.cloud || 0, currentPlan.storage.cloud === "Unlimited" ? 0 : currentPlan.storage_bytes.cloud)}%`,
            email: convertBytesToReadableStorage(service.storage_info?.email),
            email_percent: `${getPercentUsed(service.storage_info?.email || 0, currentPlan.storage.email === "Unlimited" ? 0 : currentPlan.storage_bytes.email)}%`,
            host: convertBytesToReadableStorage(service.storage_info?.host),
            host_percent: `${getPercentUsed(service.storage_info?.host || 0, currentPlan.storage.host === "Unlimited" ? 0 : currentPlan.storage_bytes.host)}%`,
            file: convertBytesToReadableStorage(service.storage_info?.file),
            file_percent: `${getPercentUsed(service.storage_info?.file || 0, currentPlan.storage.file === "Unlimited" ? 0 : currentPlan.storage_bytes.file)}%`,
        }
    }
    return {
        currentPlan,
        serviceStatus
    };
}

function getPercentUsed(used: number, total: number) {
    // An unknown ceiling reads as "no bar", the same as an unlimited one. The
    // table's limits are blank until GET /plan answers, and without this the
    // dashboard renders "NaN%" in that window.
    if (!Number.isFinite(total) || total === 0) return 0;
    let percent = (used / total) * 100;
    if (used > 0 && percent < 1) return 1;
    return Math.round(percent);
}

export function convertBytesToReadableStorage(bytes: number) {
    if (!bytes) return "0B";
    if (bytes >= 1024 ** 4) return (bytes / (1024 ** 4)).toFixed(2) + "TB";
    if (bytes >= 1024 ** 3) return (bytes / (1024 ** 3)).toFixed(2) + "GB";
    if (bytes >= 1024 ** 2) return (bytes / (1024 ** 2)).toFixed(2) + "MB";
    if (bytes >= 1024) return (bytes / 1024).toFixed(2) + "KB";
    return bytes + "B";
}

/**
 * Turn one plan's authored fields into everything drawn from them.
 *
 * Extracted from the IIFE below so it can run TWICE: once on the built-in table
 * at module load, and again on whatever `GET /plan` returns. `storage_bytes`,
 * `users` and `offer_list` are all derived, so replacing a limit without
 * re-deriving would leave the meters and the marketing list quoting the old one.
 */
function derivePlan(item: any) {
    function storageUnitConverter(storage: string) {
        // convert "1GB" to 1073741824
        if (storage === "Unlimited") return 0;

        let unit = storage.replace(/[\d.\s]/g, "").toUpperCase(); // "GB", "TB"
        let number = parseFloat(storage.replace(/[^\d.]/g, "")); // 1

        let multiplier = ({
            B: 1,
            KB: 1024,
            MB: 1024 ** 2,
            GB: 1024 ** 3,
            TB: 1024 ** 4,
            PB: 1024 ** 5,
        })[unit];

        if (!multiplier || Number.isNaN(number)) {
            return NaN;
        }

        return number * multiplier;
    }

    {
        item.storage_bytes = {
            database: storageUnitConverter(item.storage.database),
            cloud: storageUnitConverter(item.storage.cloud),
            host: storageUnitConverter(item.storage.host),
            email: storageUnitConverter(item.storage.email),
            file: storageUnitConverter(item.storage.file),
        }
        // Metered per MONTH, unlike everything above, which is a level held. Read
        // through the same converter so "Unlimited" lands on 0 and drops its line,
        // exactly as the storage figures do.
        item.egress_bytes = storageUnitConverter(item.egress || "Unlimited");
        function convertUsers(users: number | string) {
            if (typeof users === "number") return users;
            if (typeof users === "string") {
                let canBeNumber = !isNaN(parseFloat(users));
                if (canBeNumber) return parseFloat(users);

                if (users.toLowerCase() === "unlimited") return 0;
                let multiplier = ({
                    k: 1e3,
                    m: 1e6,
                    b: 1e9,
                    t: 1e12,
                })[users.slice(-1).toLowerCase()];
                let number = parseFloat(users.slice(0, -1));
                if (!multiplier || Number.isNaN(number)) {
                    return NaN;
                }
                return number * multiplier;
            }
            return NaN;
        }
        item.users = convertUsers(item.users_string);
        item.offer_list = [];

        if (item.users > 0) {
            item.offer_list.push({
                class: "user",
                text: `${item.users_string} User Accounts`,
            });
        }

        if (item.storage_bytes.database > 0) {
            item.offer_list.push({
                class: "data",
                text: `${item.storage.database} Database Storage`,
            });
        }

        if (item.storage_bytes.cloud > 0) {
            item.offer_list.push({
                class: "file",
                text: `${item.storage.cloud} File Storage`,
            });
        }

        if (item.storage_bytes.email > 0) {
            item.offer_list.push({
                class: "mail",
                text: `${item.storage.email} Email Storage`,
            });
        }

        if (item.storage_bytes.host > 0) {
            item.offer_list.push({
                class: "global",
                text: `${item.storage.host} Web Hosting`,
            });
        }

        if (item.egress_bytes > 0) {
            item.offer_list.push({
                class: "swap",
                text: `${item.egress} Monthly Bandwidth`,
            });
        }

        if (item.email_sends > 0) {
            item.offer_list.push({
                class: "mail",
                // Grouped by thousands rather than left as 50000. Done here and
                // not with toLocaleString so the separator cannot change with the
                // visitor's locale while the rest of the card stays in English.
                text: `${String(item.email_sends).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} Monthly Email Sends`,
            });
        }

        let label = item.label.toLowerCase();

        // if(!label.includes("free")) {
        //     item.offer_list.push({
        //         class: "setting",
        //         text: "Register Unlimited Client Secret Keys",
        //     });
        // }

        if (item.overages) {
            item.offer_list.push({
                class: "billing",
                // The point of this line is the CONTRAST with Free, which the old
                // wording left unsaid: plan.py's table reads "Past quota: hard
                // stop / overage per GB / overage per GB" across Free, Standard
                // and Premium. On a paid plan nothing stops when a limit is
                // reached; the excess is billed instead. Say that, rather than
                // just naming the metered resources.
                text: "No hard stop at your limits: extra storage, files, bandwidth, users and email sends are billed as you go (currently free)",
            });
        }
    }
    return item;
}

/**
 * The plan table.
 *
 * REACTIVE, because the numbers below are only the fallback. The authority is
 * the backend's plan.py, served by `GET /plan`, and applyPlanLimits() overwrites
 * these once it answers. Components read this object directly (planCards.vue),
 * so a plain object would render the built-in numbers and never update.
 *
 * The built-ins stay for two reasons: the page renders before any request can
 * answer, and a failed request must not blank the pricing page.
 */
export const modelSpecs: any = reactive(((m: any) => {
    for (let k in m) derivePlan(m[k]);
    return m;
})({
    /* ── plan definitions ──────────────────────────────────────────────────
     *
     * EVERY LIMIT HERE IS DELIBERATELY BLANK. The numbers live in the backend's
     * plan.py, which is the thing that actually refuses an upload, and they
     * arrive through GET /plan (see loadPlanSpecs below). This file used to keep
     * a second hand-maintained copy, and the two drifted: the page quoted quotas
     * the server did not grant.
     *
     * So blank is not "missing", it is "not mine to state". A card renders its
     * price and description immediately and gains its quota lines when the
     * request answers; the `> 0` guards in derivePlan skip every line whose
     * value is still unknown, and getPercentUsed reads an unknown ceiling as no
     * bar rather than NaN.
     *
     * What IS authored here is what plan.py does not carry: price, description,
     * label, and the overages flag. Fill a limit back in and you have recreated
     * the drift, because nothing will ever tell you it disagrees with the server.
     */
    "Free": {
        price: 0,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: ""
        },
        egress: "",
        email_sends: null,
        description: "Perfect for testing and small projects. Free forever.",
        label: "Free"
    },
    "Standard": {
        price: 19,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: ""
        },
        egress: "",
        email_sends: null,
        description: "Ideal for most projects. Perfect for hobby projects and learning.",
        label: "Standard",
        overages: true
    },
    "Standard (Perpetual License)": {
        price: 299,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: ""
        },
        egress: "",
        email_sends: null,
        description: "Ideal for most projects. One-time payment with lifetime access. Perfect for hobby projects and learning.",
        label: "Standard (Perpetual)",
        overages: true
    },
    "Premium": {
        price: 89,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: ""
        },
        egress: "",
        email_sends: null,
        description: "Perfect for businesses and serious projects with priority support.",
        label: "Premium",
        overages: true
    },
    "Premium (Perpetual License)": {
        price: 1399,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: ""
        },
        egress: "",
        email_sends: null,
        description: "For businesses and projects that require more resources and priority support. One-time payment with lifetime access.",
        label: "Premium (Perpetual)",
        overages: true
    },
    "Unlimited": {
        price: 599,
        users_string: "",
        storage: {
            database: "",
            cloud: "",
            email: "",
            host: "",
            file: ""
        },
        egress: "",
        email_sends: null,
        description: "For businesses and projects that require unlimited resources and priority support. Perfect for large-scale projects and enterprises.",
        label: "Unlimited"  
    }
}));


/* ── live plan limits ──────────────────────────────────────────────────────
 *
 * The table above is a fallback. The authority is the backend's plan.py, which
 * is what actually refuses an upload, and which this page has historically kept
 * a second hand-maintained copy of. Two copies with nothing keeping them in step
 * is how a page promises a quota the server rejects.
 */

/** Which table entries a served plan group governs.
 *
 * The perpetual-licence entries are not separate plans on the backend: groups 51
 * and 52 are defined as the Standard and Premium limits, so they track the same
 * numbers and would otherwise be the last hand-maintained copies left. */
const PLAN_KEYS_BY_GROUP: { [group: number]: string[] } = {
    1: ["Free"],
    2: ["Standard", "Standard (Perpetual License)"],
    3: ["Premium", "Premium (Perpetual License)"],
    50: ["Unlimited"],
};

/** Bytes back into this table's vocabulary ("500MB", "1GB", "1TB").
 *
 * The table stores human strings and derives its byte counts from them, so the
 * fetched number has to re-enter through the same door: writing storage_bytes
 * directly would leave the strings on the cards saying something else. `null`
 * from the server means no ceiling. */
function bytesToSpecString(bytes: number | null): string {
    if (bytes === null || !Number.isFinite(bytes as number)) return "Unlimited";
    if (bytes === 0) return "0B";

    const units: [string, number][] = [
        ["TB", 1024 ** 4], ["GB", 1024 ** 3], ["MB", 1024 ** 2], ["KB", 1024], ["B", 1],
    ];
    for (const [unit, size] of units) {
        if ((bytes as number) < size) continue;
        const value = (bytes as number) / size;
        // Whole numbers where they divide evenly ("1GB", not "1.00GB"), one
        // decimal otherwise, which is what storageUnitConverter reads back.
        return `${Number.isInteger(value) ? value : Number(value.toFixed(1))}${unit}`;
    }
    return `${bytes}B`;
}

/** Counts back into this table's vocabulary ("3", "50K", "Unlimited"). */
function countToSpecString(count: number | null): string {
    if (count === null || !Number.isFinite(count as number)) return "Unlimited";
    const n = count as number;
    if (n >= 1e9) return `${Number((n / 1e9).toFixed(1))}B`;
    if (n >= 1e6) return `${Number((n / 1e6).toFixed(1))}M`;
    if (n >= 1e3) return `${Number((n / 1e3).toFixed(1))}K`;
    return String(n);
}

type ServedPlan = {
    group: number;
    key: string;
    label: string;
    limits: {
        users: number | null;
        dbst: number | null;
        cldst: number | null;
        emlst: number | null;
        hstst: number | null;
        dbfst: number | null;
        egres?: number | null;
        emlsd?: number | null;
    };
};

/**
 * Overwrite the built-in numbers with the served ones.
 *
 * Prices, descriptions and labels are NOT touched: plan.py does not carry them,
 * so they stay authored here. Only the limits move.
 */
export function applyPlanLimits(plans: ServedPlan[]): void {
    for (const plan of plans || []) {
        const keys = PLAN_KEYS_BY_GROUP[plan.group];
        if (!keys || !plan.limits) continue;

        for (const key of keys) {
            const item = modelSpecs[key];
            if (!item) continue;

            item.users_string = countToSpecString(plan.limits.users);
            item.storage = {
                database: bytesToSpecString(plan.limits.dbst),
                cloud: bytesToSpecString(plan.limits.cldst),
                email: bytesToSpecString(plan.limits.emlst),
                host: bytesToSpecString(plan.limits.hstst),
                file: bytesToSpecString(plan.limits.dbfst),
            };
            // Same door as the storage figures: write the authored form and let
            // derivePlan compute the bytes, so the card and the meters cannot end
            // up quoting different numbers.
            item.egress = bytesToSpecString(plan.limits.egres ?? null);
            item.email_sends = plan.limits.emlsd ?? null;

            // Re-derive: storage_bytes, users and the whole offer list come from
            // the fields just replaced.
            derivePlan(item);
        }
    }
}

let planSpecsRequest: Promise<void> | null = null;

/**
 * Pull the enforced limits and apply them. Safe to call from anywhere; the
 * request happens once per page load and is answered from the browser cache for
 * a week after that (the endpoint sets the max-age, a client cannot).
 *
 * A PLAIN fetch with no custom headers on purpose. Anything non-safelisted turns
 * this into a preflighted request, and the gateway's allow-list is a fixed
 * string that would then have to be edited to match. It also keeps the response
 * cacheable, which the SDK's POST transport could not be.
 *
 * Failure is not an error: the built-in table is already on screen, so a dead
 * network leaves the page exactly as it was.
 */
/** True once a real /plan payload has been applied. Lets a view tell "the
 *  limits are still unknown" apart from "this plan genuinely has none". */
export const planSpecsLoaded = ref(false);

export function loadPlanSpecs(): Promise<void> {
    // Only a SUCCESSFUL load is remembered. Memoizing a failure was the trap:
    // one early miss (boot before the endpoint json resolved, an offline moment,
    // a poisoned cache entry) left every card blank for the whole page life with
    // no way to retry, because the failed promise satisfied this check forever.
    // Every exit is logged, including the two that do nothing. "I see no request"
    // is otherwise indistinguishable between "already loaded", "a call is still
    // in flight" and "this never ran at all", and only the last one is a bug.
    if (planSpecsRequest && planSpecsLoaded.value) {
        console.log("[plan] already loaded, no request needed", {
            standardLines: modelSpecs["Standard"].offer_list.length,
            premiumLines: modelSpecs["Premium"].offer_list.length,
        });
        return planSpecsRequest;
    }
    if (planSpecsRequest) {
        console.log("[plan] a request is already in flight, awaiting it");
        return planSpecsRequest;
    }

    planSpecsRequest = (async () => {
        try {
            console.log("[plan] loading plan limits", {
                mode: import.meta.env.VITE_MODE,
                service: import.meta.env.VITE_ADMIN,
            });

            // Imported HERE, not at the top of the file. This module is pulled in
            // by main.ts's own import graph (main -> user -> service ->
            // service-spec), so a static `import { ENDPOINTS } from "@/main"`
            // is read while main.ts is still evaluating and throws
            // "Cannot access 'ENDPOINTS' before initialization". A dynamic
            // import resolves after that evaluation finishes, which is exactly
            // when the endpoints actually exist.
            const { ENDPOINTS } = await import("@/main");
            const endpoints: any = await ENDPOINTS;
            const base = endpoints?.admin_public_endpoint;
            console.log("[plan] admin_public_endpoint =", base);
            if (!base) throw new Error("no admin endpoint");

            // Second attempt bypasses the browser cache. This response is held for
            // a week (max-age=604800), so a copy stored while the endpoint was
            // failing or absent would otherwise keep answering for days.
            let applied = await attempt(`${base}plan`, "default");
            if (!applied) applied = await attempt(`${base}plan`, "reload");

            planSpecsLoaded.value = applied;
            if (applied) {
                console.log("[plan] limits applied", {
                    Standard: modelSpecs["Standard"].offer_list.map((o: any) => o.text),
                    Premium: modelSpecs["Premium"].offer_list.map((o: any) => o.text),
                });
            }
            if (!applied) {
                // Say WHICH backend was asked. VITE_MODE picks the admin service
                // and therefore the region: development -> eu71 (eu-west-1),
                // production -> us31 (us-east-1). /plan is served by the
                // PlanInfo function, which is not deployed in every region, so
                // naming the mode and the url turns "the cards are empty" into a
                // one-line answer instead of a network-tab hunt.
                console.warn(
                    `[plan] /plan returned nothing usable, cards will show no limits. ` +
                    `mode=${import.meta.env.VITE_MODE} service=${import.meta.env.VITE_ADMIN} url=${base}plan`,
                );
                planSpecsRequest = null; // let a later caller try again
            }
        } catch (err) {
            console.warn(
                `[plan] could not load live plan limits; using built-in table ` +
                `(mode=${import.meta.env.VITE_MODE}, service=${import.meta.env.VITE_ADMIN})`,
                err,
            );
            planSpecsRequest = null;
        }
    })();

    return planSpecsRequest;
}

async function attempt(url: string, cache: RequestCache): Promise<boolean> {
    try {
        console.log(`[plan] GET ${url} (cache: ${cache})`);
        const res = await fetch(url, { cache });
        console.log(`[plan] <- ${res.status} ${res.statusText}`);
        if (!res.ok) return false;
        const body = await res.json();
        console.log("[plan] payload", body);
        if (!body || !Array.isArray(body.plans) || !body.plans.length) {
            console.warn("[plan] payload had no plans array");
            return false;
        }
        applyPlanLimits(body.plans);
        return true;
    } catch (err) {
        console.warn(`[plan] fetch failed (cache: ${cache})`, err);
        return false;
    }
}

// Kicked off on import. Every consumer of this module renders plan numbers, and
// the fetch is one cached GET a week.
void loadPlanSpecs();
